import { javascript } from '@codemirror/lang-javascript';
import { EditorState } from '@codemirror/state';
import { css, html, LitElement } from 'lit';
import { format as prettierFormat } from 'prettier/standalone';
import '@spectrum-web-components/dialog/sp-dialog.js';
import '@spectrum-web-components/underlay/sp-underlay.js';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import {
  placeholder as cmPlaceholder,
  Decoration,
  type DecorationSet,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  type ViewUpdate,
} from '@codemirror/view';
import { tags as t } from '@lezer/highlight';
import * as prettierBabel from 'prettier/plugins/babel';
import * as prettierEstree from 'prettier/plugins/estree';

const codeHighlightStyle = HighlightStyle.define([
  {
    tag: [t.variableName, t.propertyName, t.attributeName],
    color: 'var(--color-syntax-attr, #0969da)',
  },
  {
    tag: [t.function(t.variableName), t.function(t.propertyName), t.labelName],
    color: 'var(--color-syntax-attr, #0969da)',
  },
  {
    tag: [t.keyword, t.modifier, t.operatorKeyword],
    color: 'var(--color-syntax-literal, #cf222e)',
  },
  {
    tag: [t.string, t.special(t.string)],
    color: 'var(--color-syntax-string, #7c3aed)',
  },
  {
    tag: [t.number, t.integer, t.float],
    color: 'var(--color-syntax-number, #0550ae)',
  },
  {
    tag: [t.bool, t.null, t.atom],
    color: 'var(--color-syntax-literal, #cf222e)',
  },
  {
    tag: [t.comment],
    color: 'var(--color-syntax-punctuation, #6b7280)',
    fontStyle: 'italic',
  },
  {
    tag: [t.punctuation, t.bracket],
    color: 'var(--color-syntax-punctuation, #6b7280)',
  },
]);

const handlebarsDecorator = new MatchDecorator({
  regexp: /\{\{[^}]*\}\}/g,
  decoration: Decoration.mark({ class: 'cm-handlebars-token' }),
});

const handlebarsHighlightPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = handlebarsDecorator.createDeco(view);
    }

    update(update: ViewUpdate) {
      this.decorations = handlebarsDecorator.updateDeco(
        update,
        this.decorations
      );
    }
  },
  {
    decorations: (v) => v.decorations,
  }
);

export class SbCodeEditor extends LitElement {
  static properties = {
    name: {},
    value: {},
    placeholder: {},
    mode: {},
    rows: { type: Number },
    maxRows: { type: Number, attribute: 'max-rows' },
    disabled: { type: Boolean, reflect: true },
    fullscreen: { type: Boolean, reflect: true },
  } as const;

  declare name?: string;
  declare value: string;
  declare placeholder: string;
  declare mode: 'javascript' | 'template';
  declare rows: number;
  declare maxRows: number;
  declare disabled: boolean;
  declare fullscreen: boolean;

  private editor?: EditorView;
  private syncingFromEditor = false;

  static styles = css`
    /* Base editor surface */
    :host {
      display: block;
      width: 100%;
      --code-editor-min-height: 160px;
      --code-editor-max-height: 400px;
      --code-editor-surface-background: var(
        --mod-textfield-background-color,
        var(--spectrum-textfield-background-color, var(--color-card-bg, #ffffff))
      );
      --code-editor-caret-width: 2px;
      --kv-dialog-bg: var(--code-editor-surface-background);
      --code-editor-caret-color: var(
        --mod-textfield-text-color-default,
        var(
          --highcontrast-textfield-text-color-default,
          var(--color-app-text, #1f1f1f)
        )
      );
      --code-editor-caret-color-focus: var(
        --mod-textfield-text-color-keyboard-focus,
        var(
          --highcontrast-textfield-text-color-keyboard-focus,
          var(--color-app-text, #1f1f1f)
        )
      );
    }

    /* CodeMirror textfield styling */
    #editor-host {
      width: 100%;
    }

    .cm-editor {
      border:
        var(--mod-textfield-border-width, var(--spectrum-textfield-border-width))
        solid
        var(
          --highcontrast-textfield-border-color,
          var(
            --mod-textfield-border-color,
            var(--spectrum-textfield-border-color, var(--color-border, #b8b8b8))
          )
        );
      background: var(--code-editor-surface-background);
      color: var(
        --highcontrast-textfield-text-color-default,
        var(
          --mod-textfield-text-color-default,
          var(--spectrum-textfield-text-color-default)
        )
      );
      caret-color: var(--code-editor-caret-color);
      font-size: 12px;
      border-radius: var(
        --mod-textfield-corner-radius,
        var(--spectrum-textfield-corner-radius)
      );
    }

    .cm-editor:hover {
      border-color: var(
        --highcontrast-textfield-border-color-hover,
        var(
          --mod-textfield-border-color-hover,
          var(
            --spectrum-textfield-border-color-hover,
            var(--color-border-hover, #8c8c8c)
          )
        )
      );
      color: var(
        --highcontrast-textfield-text-color-hover,
        var(
          --mod-textfield-text-color-hover,
          var(--spectrum-textfield-text-color-hover)
        )
      );
    }

    .cm-editor.cm-focused {
      outline: none;
      border-color: var(
        --highcontrast-textfield-border-color-keyboard-focus,
        var(
          --mod-textfield-border-color-keyboard-focus,
          var(
            --spectrum-textfield-border-color-keyboard-focus,
            var(--color-primary, #0a84ff)
          )
        )
      );
      box-shadow: 0 0 0
        var(
          --mod-textfield-focus-indicator-width,
          var(--spectrum-textfield-focus-indicator-width)
        )
        var(
          --highcontrast-textfield-focus-indicator-color,
          var(
            --mod-textfield-focus-indicator-color,
            var(--spectrum-textfield-focus-indicator-color)
          )
        );
      color: var(
        --highcontrast-textfield-text-color-keyboard-focus,
        var(
          --mod-textfield-text-color-keyboard-focus,
          var(--spectrum-textfield-text-color-keyboard-focus)
        )
      );
    }

    .cm-gutters {
      display: none;
    }

    .cm-scroller {
      overflow: auto;
      min-height: var(--code-editor-min-height);
      max-height: var(--code-editor-max-height);
      font-family: var(
        --mono,
        'SFMono-Regular',
        Menlo,
        Monaco,
        Consolas,
        'Liberation Mono',
        monospace
      );
      line-height: 1.45;
    }

    .cm-content {
      padding: var(--gap-2, 8px);
      padding-left: 0;
      caret-color: var(--code-editor-caret-color);
    }

    .cm-line {
      padding-left: 0 !important;
    }

    .cm-editor.cm-focused .cm-content {
      caret-color: var(--code-editor-caret-color-focus);
    }

    .cm-cursor {
      border-left-width: var(--code-editor-caret-width);
      border-left-color: var(--code-editor-caret-color);
    }

    .cm-editor.cm-focused .cm-cursor {
      border-left-width: var(--code-editor-caret-width);
      border-left-color: var(--code-editor-caret-color-focus);
    }

    .cm-dropCursor {
      border-left-width: var(--code-editor-caret-width);
      border-left-color: var(--code-editor-caret-color-focus);
    }

    .cm-handlebars-token {
      color: var(--color-syntax-string, #7c3aed);
      font-weight: 600;
    }

    /* Fullscreen dialog shell */
    .fullscreen-overlay {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: grid;
      place-items: center;
      padding: var(--gap-4);
      box-sizing: border-box;
    }

    .fullscreen-overlay sp-underlay {
      z-index: 1;
      background-color: var(--color-modal-overlay);
    }

    .fullscreen-overlay .dialog-container {
      position: relative;
      z-index: 2;
      width: 100%;
      height: 100%;
      display: grid;
      place-items: center;
    }

    .fullscreen-overlay sp-dialog {
      width: calc(100vw - 2 * var(--gap-4));
      max-height: calc(100vh - 2 * var(--gap-4));
      background: var(--kv-dialog-bg, var(--color-card-bg));
      color: var(--color-app-text);
      box-shadow: var(--color-modal-shadow);
    }

    .fullscreen-content {
      min-height: min(70vh, calc(100vh - 2 * var(--gap-4) - 120px));
      max-height: calc(100vh - 2 * var(--gap-4) - 120px);
      width: 100%;
      box-sizing: border-box;
      padding: var(--gap-2, 8px);
    }

    .fullscreen-content #editor-host {
      height: 100%;
    }

    :host([fullscreen]) {
      --code-editor-min-height: 100%;
      --code-editor-max-height: 100%;
    }

    :host([fullscreen]) .cm-editor,
    :host([fullscreen]) .cm-scroller {
      height: 100%;
      max-height: none;
      min-height: 100%;
    }
  `;

  constructor() {
    super();
    this.name = undefined;
    this.value = '';
    this.placeholder = '';
    this.mode = 'javascript';
    this.rows = 8;
    this.maxRows = 20;
    this.disabled = false;
    this.fullscreen = false;
  }

  firstUpdated() {
    this.applyEditorHeightBounds();
    this.createEditor();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.editor?.destroy();
    this.editor = undefined;
  }

  override focus(options?: FocusOptions): void {
    if (this.disabled) return;
    if (this.editor) {
      this.editor.focus();
      return;
    }
    super.focus(options);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('rows') || changed.has('maxRows')) {
      this.applyEditorHeightBounds();
    }

    if (this.shouldRecreateEditor(changed) && this.editor) {
      this.recreateEditor();
      return;
    }

    if (changed.has('value')) {
      this.syncEditorValueFromProperty();
    }
  }

  render() {
    return this.fullscreen
      ? this.renderFullscreenDialog()
      : this.renderInlineEditor();
  }

  private closeFullscreen = (e?: Event) => {
    e?.stopPropagation();
    if (!this.fullscreen) return;
    this.fullscreen = false;
    this.dispatchEvent(
      new CustomEvent('fullscreen-change', {
        detail: { fullscreen: false },
        bubbles: true,
        composed: true,
      })
    );
  };

  private onOverlayClick = () => {
    this.closeFullscreen();
  };

  private onDialogContainerClick = (e: Event) => {
    e.stopPropagation();
  };

  private renderInlineEditor() {
    return html`<div id="editor-host"></div>`;
  }

  private onFullscreenContentClick = (e: Event) => {
    const editorHost = this.renderRoot.querySelector('#editor-host');
    if (editorHost?.contains(e.target as Node)) {
      return;
    }
    if (!this.disabled && this.editor) {
      const end = this.editor.state.doc.length;
      this.editor.dispatch({
        selection: { anchor: end, head: end },
      });
      this.editor.focus();
    }
  };

  private renderFullscreenDialog() {
    const heading = this.name?.trim() || 'Editor';

    return html`
      <div class="fullscreen-overlay" @click=${this.onOverlayClick}>
        <sp-underlay open></sp-underlay>
        <div class="dialog-container" @click=${this.onDialogContainerClick}>
          <sp-dialog
            open
            size="l"
            dismissable
            role="dialog"
            aria-label=${`Editor for ${heading}`}
            @close=${this.closeFullscreen}
          >
            <h2 slot="heading">${heading}</h2>
            <div class="fullscreen-content" @click=${this.onFullscreenContentClick}>
              <div id="editor-host"></div>
            </div>
          </sp-dialog>
        </div>
      </div>
    `;
  }

  private shouldRecreateEditor(changed: Map<string, unknown>): boolean {
    return (
      changed.has('placeholder') ||
      changed.has('mode') ||
      changed.has('disabled') ||
      changed.has('fullscreen')
    );
  }

  private syncEditorValueFromProperty() {
    if (!this.editor || this.syncingFromEditor) return;

    const current = this.editor.state.doc.toString();
    if (current === this.value) return;
    this.editor.dispatch({
      changes: { from: 0, to: current.length, insert: this.value ?? '' },
    });
  }

  private buildEditorExtensions() {
    const extensions = [
      EditorState.readOnly.of(this.disabled),
      EditorView.editable.of(!this.disabled),
      EditorView.lineWrapping,
      EditorView.domEventHandlers({
        blur: (_event, view) => {
          void this.handleEditorBlur(view);
          return false;
        },
      }),
      EditorView.updateListener.of((update) => {
        if (!update.docChanged) return;
        const nextValue = update.state.doc.toString();
        if (nextValue === this.value) return;
        this.syncingFromEditor = true;
        this.value = nextValue;
        this.syncingFromEditor = false;
        this.dispatchEvent(
          new Event('input', { bubbles: true, composed: true })
        );
      }),
    ];

    if (this.mode === 'javascript') {
      extensions.push(javascript(), syntaxHighlighting(codeHighlightStyle));
    } else if (this.mode === 'template') {
      extensions.push(handlebarsHighlightPlugin);
    }

    if (this.placeholder) {
      extensions.push(cmPlaceholder(this.placeholder));
    }

    return extensions;
  }

  private createEditor() {
    const host = this.renderRoot.querySelector(
      '#editor-host'
    ) as HTMLDivElement;
    if (!host) return;

    this.editor = new EditorView({
      state: EditorState.create({
        doc: this.value ?? '',
        extensions: this.buildEditorExtensions(),
      }),
      parent: host,
    });
  }

  private recreateEditor() {
    this.editor?.destroy();
    this.editor = undefined;
    this.createEditor();
  }

  private async handleEditorBlur(view: EditorView): Promise<void> {
    await this.commitEditor(view);
    this.dispatchEvent(
      new CustomEvent('editor-blur', { bubbles: true, composed: true })
    );
  }

  private async commitEditor(
    view: EditorView | undefined = this.editor
  ): Promise<void> {
    if (!view) return;

    if (this.mode === 'javascript') {
      await this.formatJavaScriptInView(view);
    }

    this.dispatchEvent(
      new CustomEvent('editor-commit', {
        detail: {
          value: view.state.doc.toString(),
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async formatJavaScriptInView(view: EditorView): Promise<void> {
    const source = view.state.doc.toString();
    if (!source.trim()) return;

    try {
      const formatted = await this.formatJavaScriptLikeSource(source);
      if (!formatted || formatted === source) return;
      if (this.editor !== view) return;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: formatted },
      });
    } catch {
      // Skip formatting when the editor contains incomplete/invalid JS.
    }
  }

  private async formatJavaScriptLikeSource(source: string): Promise<string> {
    if (this.isObjectLiteralLike(source)) {
      const formattedObject = await this.tryFormatWrappedExpression(source);
      if (formattedObject !== undefined) {
        return formattedObject;
      }
    }

    try {
      return await prettierFormat(source, {
        parser: 'babel',
        plugins: [prettierBabel, prettierEstree],
      });
    } catch {
      const formattedObject = await this.tryFormatWrappedExpression(source);
      if (formattedObject !== undefined) {
        return formattedObject;
      }
      throw new Error('Unable to format JavaScript source');
    }
  }

  private async tryFormatWrappedExpression(
    source: string
  ): Promise<string | undefined> {
    try {
      const wrapped = `(${source})`;
      const formattedWrapped = await prettierFormat(wrapped, {
        parser: 'babel',
        plugins: [prettierBabel, prettierEstree],
      });
      return this.unwrapFormattedExpression(formattedWrapped);
    } catch {
      return undefined;
    }
  }

  private isObjectLiteralLike(source: string): boolean {
    const trimmed = source.trim();
    return (
      trimmed.startsWith('{') && trimmed.endsWith('}') && trimmed.includes(':')
    );
  }

  private unwrapFormattedExpression(formattedWrapped: string): string {
    const match = formattedWrapped.match(/^\(([\s\S]*)\);\s*$/);
    if (!match) return formattedWrapped;
    return match[1].trim();
  }

  private applyEditorHeightBounds() {
    const lineHeight = 20;
    const minRows = Math.max(1, Number.isFinite(this.rows) ? this.rows : 8);
    const maxRows = Math.max(
      minRows,
      Number.isFinite(this.maxRows) ? this.maxRows : 20
    );

    this.style.setProperty(
      '--code-editor-min-height',
      `${minRows * lineHeight}px`
    );
    this.style.setProperty(
      '--code-editor-max-height',
      `${maxRows * lineHeight}px`
    );
  }
}

customElements.define('sb-code-editor', SbCodeEditor);
